/**
 * `AppBridge` — the page half of the direct bridge (PRD `agentic-app-direct-bridge` FR-1…FR-14).
 *
 * An app registers its tool handlers and resources, then calls {@link AppBridge.start}. The Talkie host
 * calls those handlers directly over the signed postMessage envelope; no MCP server runs in the page.
 *
 *  - No bootstrap in the URL hash ⇒ `standalone`: nothing is installed or sent (FR-14). A malformed
 *    bootstrap disables the integration (`error`), never the app.
 *  - The inbound listener is installed first, then `hello` is sent and repeated with backoff (fresh
 *    envelope nonce, the SAME page instance) until a matching `welcome` arrives (FR-3). A new instance
 *    would read to the host as a reload (FR-13), so the instance never changes for the page's life.
 *  - A refused `welcome`, or none inside `readyTimeoutMs` (an older Talkie that still speaks MCP), ends
 *    in `needs-newer-talkie`, and the page stops sending (Q7). It never falls back to MCP.
 *  - A handler runs with an `AbortSignal` the host's `cancel` aborts (FR-6); a result for a cancelled or
 *    closed call is never sent. A call for another generation is refused before any handler runs (FR-13).
 *  - The app's own failure is a `result` with `isError` (a thrown error, or {@link ToolError} when the
 *    model may usefully try again); `error` is reserved for protocol faults (FR-5).
 *  - The page asks the host for what only it can do — a declared grant, a sign-in, an external link —
 *    and may cancel its own request (FR-9…FR-11).
 */

import { NonceCache, randomUuid, sign, verify } from "./envelope.js";
import {
  appMessage,
  encodedBytes,
  isBridgeCode,
  MAX_CODE_LEN,
  MAX_HANDLERS,
  MAX_HELLO_BYTES,
  MAX_MESSAGE_BYTES,
  MAX_RESOURCES,
  MAX_TOKEN_LEN,
  PAGE_CODES,
  parseHostMessage,
  type AppToHost,
  type CallMsg,
  type Hello,
  type HostCapabilities,
  type McpResult,
  type ReadMsg,
  type RequestKind,
  type Welcome,
} from "./protocol.js";
import { declarationIsValid, declarationMatches, templateVariables } from "./resource_template.js";
import { parseAgenticAppHash, type ParsedAgenticAppHash } from "./url_hash.js";

export type BridgeStatus = "standalone" | "connecting" | "connected" | "needs-newer-talkie" | "error";

/** `_meta` key the host reads on an `isError` result (`dobby-agentic-app-bridge` `convert.rs`). */
export const OUTCOME_META_KEY = "com.resatech.talkie/outcome";

export interface ToolContext {
  /** Aborted when the host stops waiting for this call, or the page closes (FR-6). */
  readonly signal: AbortSignal;
  /** The host is holding the run for the person (trusted `presents_ui`, FR-7). */
  readonly waitsOnPerson: boolean;
}
export interface ResourceContext {
  readonly signal: AbortSignal;
  /** The values the URI bound to the declaration's `{name}` variables (empty for an exact URI). */
  readonly variables: Readonly<Record<string, string>>;
}
export type ToolHandler = (args: unknown, ctx: ToolContext) => Promise<McpResult> | McpResult;
/** Returns a `ReadResourceResult` (`{ contents: [...] }`). A throw is reported as `resource_unavailable`. */
export type ResourceHandler = (uri: string, ctx: ResourceContext) => Promise<McpResult> | McpResult;

/**
 * The app's own failure. Thrown from a handler it becomes an `isError` result; `retryable` tells the
 * host the model may propose the call again (`FAILED_RETRYABLE`), otherwise it ends `FAILED_FINAL`.
 * Any other thrown error is final.
 */
export class ToolError extends Error {
  readonly retryable: boolean;
  constructor(message: string, options: { readonly retryable?: boolean } = {}) {
    super(message);
    this.name = "ToolError";
    this.retryable = options.retryable ?? false;
  }
}

/** The host refused or failed one of the page's requests; `code` is stable (`protocol.rs` `codes`). */
export class HostError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "HostError";
  }
}

export interface BridgeOptions {
  /** The version of the app bundle this page was built from (FR-4). */
  readonly appVersion: string;
  /** Defaults to `window.location.hash`. */
  readonly hash?: string;
  /** The host frame. Defaults to `window.parent`. */
  readonly peerWindow?: Window;
  /** Where the page listens. Defaults to `window`. */
  readonly hostWindow?: Window;
  /** Defaults to `window.location.origin`. */
  readonly ourOrigin?: string;
  /** Defaults to a fresh UUID: one per page load. */
  readonly instance?: string;
  /** First `hello` repeat delay; doubles per repeat up to {@link helloMaxDelayMs}. Default 250 ms. */
  readonly helloInitialDelayMs?: number;
  /** Default 5 s. */
  readonly helloMaxDelayMs?: number;
  /** No `welcome` by then ⇒ `needs-newer-talkie`. Default 120 s (the host's attach bound, FR-2). */
  readonly readyTimeoutMs?: number;
  readonly nowMs?: () => number;
  readonly onStatus?: (status: BridgeStatus, detail?: string) => void;
}

/** What the library refused or dropped, by reason. Diagnostics only. */
export interface BridgeCounters {
  /** Not a host → app message (FR-1). */
  unknown: number;
  /** Anything but `welcome` before the host accepted the page. */
  early: number;
  /** A `call`/`read` for another generation (FR-13). */
  stale: number;
  /** A result for a call the host cancelled, or the page closed (FR-6). */
  lateDropped: number;
  /** A `response` for no outstanding request. */
  orphanResponses: number;
  /** A `call`/`read` reusing an id still in flight. */
  duplicates: number;
  /** An envelope that failed verification. */
  badEnvelope: number;
}

export interface GrantStatus {
  readonly connected: boolean;
  readonly access_expires_at_ms: number | null;
  readonly refresh_expires_at_ms: number | null;
  readonly has_refresh_token: boolean;
}
export interface GrantApiResponse {
  readonly status: number;
  readonly body: unknown;
}
/** The host's answer to `connect`: the person connected, or closed the sign-in. A provider's refusal is an error (`provider_refused`), never an outcome. */
export type ConnectOutcome = "accepted" | "cancelled";

/** A declared grant, used through the host: the token never reaches the page (FR-9). */
export interface GrantHandle {
  /** POST `body` to the grant's declared API endpoint. */
  call(body: unknown, options?: { readonly signal?: AbortSignal }): Promise<GrantApiResponse>;
  status(options?: { readonly signal?: AbortSignal }): Promise<GrantStatus>;
  revoke(options?: { readonly signal?: AbortSignal }): Promise<void>;
}

interface Pending {
  resolve(value: unknown): void;
  reject(error: Error): void;
}

const DEFAULT_READY_TIMEOUT_MS = 120_000;

function isToken(value: string): boolean {
  return value.length > 0 && value.length <= MAX_TOKEN_LEN;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** `isError` result for a handler's failure (FR-5). */
export function toolErrorResult(error: unknown): McpResult {
  const result: Record<string, unknown> = { isError: true, content: [{ type: "text", text: errorText(error) }] };
  if (error instanceof ToolError && error.retryable) result._meta = { [OUTCOME_META_KEY]: "retryable" };
  return result;
}

function abortError(): DOMException {
  return new DOMException("aborted", "AbortError");
}

export class AppBridge {
  readonly counters: BridgeCounters = {
    unknown: 0,
    early: 0,
    stale: 0,
    lateDropped: 0,
    orphanResponses: 0,
    duplicates: 0,
    badEnvelope: 0,
  };
  readonly instance: string;
  /** Resolves with what this host offers once it accepts the page; rejects on refusal or timeout. */
  readonly ready: Promise<HostCapabilities>;

  private readonly tools = new Map<string, ToolHandler>();
  private readonly resources: Array<{ readonly declaration: string; readonly handler: ResourceHandler }> = [];
  private readonly inflight = new Map<string, AbortController>();
  private readonly pending = new Map<string, Pending>();
  private readonly nonces = new NonceCache();
  private boot: ParsedAgenticAppHash | null = null;
  private listener: ((event: MessageEvent) => void) | null = null;
  private pagehide: (() => void) | null = null;
  private helloTimer: ReturnType<typeof setTimeout> | null = null;
  private readyTimer: ReturnType<typeof setTimeout> | null = null;
  private generation: number | null = null;
  private capabilities: HostCapabilities | null = null;
  private started = false;
  private closed = false;
  private seq = 0;
  private statusValue: BridgeStatus = "connecting";
  private readyResolve!: (caps: HostCapabilities) => void;
  private readyReject!: (error: Error) => void;

  constructor(private readonly opts: BridgeOptions) {
    if (!isToken(opts.appVersion)) throw new TypeError("appVersion must be 1…512 characters");
    this.instance = opts.instance ?? randomUuid();
    if (!isToken(this.instance)) throw new TypeError("instance must be 1…512 characters");
    this.ready = new Promise((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
    this.ready.catch(() => undefined); // surfaced through `status`; an unhandled rejection helps nobody
  }

  get status(): BridgeStatus {
    return this.statusValue;
  }
  /** What the host offers (D6), once connected. Branch on this, never on a platform name. */
  get host(): HostCapabilities | null {
    return this.capabilities;
  }

  /** Register the handler for one tool the app's manifest declares. Before {@link start} only. */
  registerTool(name: string, handler: ToolHandler): void {
    this.assertRegistering();
    if (!isBridgeCode(name)) throw new TypeError(`tool name must be 1…${MAX_CODE_LEN} ASCII characters from [A-Za-z0-9_.-]`);
    if (this.tools.has(name)) throw new TypeError(`tool ${name} is already registered`);
    if (this.tools.size >= MAX_HANDLERS) throw new RangeError(`at most ${MAX_HANDLERS} tool handlers`);
    this.tools.set(name, handler);
  }

  /**
   * Serve an exact resource URI, or a URI template (`argus://{authority}/snapshots/{id}`: a variable is
   * one segment, the scheme is literal). Before {@link start} only.
   */
  registerResource(declaration: string, handler: ResourceHandler): void {
    this.assertRegistering();
    if (!isToken(declaration) || !declarationIsValid(declaration)) {
      throw new TypeError(`resource declaration ${JSON.stringify(declaration)} is not an exact URI or a supported template`);
    }
    if (this.resources.some((r) => r.declaration === declaration)) throw new TypeError(`resource ${declaration} is already registered`);
    if (this.resources.length >= MAX_RESOURCES) throw new RangeError(`at most ${MAX_RESOURCES} resource declarations`);
    this.resources.push({ declaration, handler });
  }

  /** Begin the integration. Idempotent. */
  start(): void {
    if (this.started) return;
    this.started = true;
    const hash = this.opts.hash ?? globalThis.location?.hash ?? "";
    if (!/(^|[#&])(v|k|o)=/.test(hash)) return this.setStatus("standalone");
    try {
      this.boot = parseAgenticAppHash(hash);
    } catch (error) {
      return this.fail("error", errorText(error));
    }
    const hello = this.hello();
    if (encodedBytes(hello) > MAX_HELLO_BYTES) return this.fail("error", `hello exceeds ${MAX_HELLO_BYTES} bytes`);
    this.setStatus("connecting");
    const hostWindow = this.hostWindow();
    this.listener = (event: MessageEvent) => this.onMessage(event);
    hostWindow.addEventListener("message", this.listener);
    this.pagehide = () => this.close();
    hostWindow.addEventListener("pagehide", this.pagehide);
    this.repeatHello(hello, this.opts.helloInitialDelayMs ?? 250);
    this.readyTimer = setTimeout(() => {
      this.stopHello();
      this.fail("needs-newer-talkie", "no welcome from the host");
    }, this.opts.readyTimeoutMs ?? DEFAULT_READY_TIMEOUT_MS);
  }

  /** A declared grant, used through the host (FR-9). */
  grant(name: string): GrantHandle {
    return {
      call: async (body, options) =>
        (await this.request("grant", { grant: name, op: "call", body: body ?? {} }, options)) as GrantApiResponse,
      status: async (options) => (await this.request("grant", { grant: name, op: "status", body: {} }, options)) as GrantStatus,
      revoke: async (options) => {
        await this.request("grant", { grant: name, op: "revoke", body: {} }, options);
      },
    };
  }

  /** Start the host's sign-in for a declared grant (FR-10). Aborting `signal` cancels the request. */
  async connect(grant: string, options: { readonly signal?: AbortSignal } = {}): Promise<ConnectOutcome> {
    const result = (await this.request("connect", { grant }, options)) as { outcome?: unknown };
    const outcome = result?.outcome;
    if (outcome === "accepted" || outcome === "cancelled") return outcome;
    throw new HostError("invalid_response", "the host answered connect without an outcome");
  }

  /** Open an absolute https link outside the frame (FR-11). */
  async openLink(url: string, options: { readonly signal?: AbortSignal } = {}): Promise<void> {
    await this.request("open_link", { url }, options);
  }

  /** Local teardown (pagehide): abort every handler, settle every wait, release the listeners. Idempotent. */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.stopHello();
    if (this.readyTimer) clearTimeout(this.readyTimer);
    for (const controller of this.inflight.values()) controller.abort();
    this.inflight.clear();
    for (const p of this.pending.values()) p.reject(new HostError("closed", "the page closed"));
    this.pending.clear();
    if (this.statusValue === "connecting") this.readyReject(new HostError("closed", "the page closed"));
    const hostWindow = this.listener || this.pagehide ? this.hostWindow() : null;
    if (this.listener) hostWindow?.removeEventListener("message", this.listener);
    if (this.pagehide) hostWindow?.removeEventListener("pagehide", this.pagehide);
    this.listener = null;
    this.pagehide = null;
  }

  // ───────────────────────────── internals ──

  /**
   * The host stopped waiting for `id` (FR-6): abort its handler. A seam, not a switch: a test subclass
   * overrides it to watch the defect it guards against (a handler that keeps running) actually happen.
   */
  protected onHostCancel(id: string): void {
    this.inflight.get(id)?.abort();
  }

  private assertRegistering(): void {
    if (this.started) throw new Error("register handlers before start(): hello has already named them");
  }

  private hostWindow(): Window {
    return this.opts.hostWindow ?? (globalThis as unknown as { window: Window }).window;
  }

  private peerWindow(): Window {
    return this.opts.peerWindow ?? (globalThis as unknown as { window: Window }).window.parent;
  }

  private ourOrigin(): string {
    return this.opts.ourOrigin ?? globalThis.location.origin;
  }

  private setStatus(status: BridgeStatus, detail?: string): void {
    this.statusValue = status;
    this.opts.onStatus?.(status, detail);
  }

  private fail(status: "needs-newer-talkie" | "error", detail: string, code = status === "error" ? "bridge_error" : "no_welcome"): void {
    this.setStatus(status, detail);
    this.readyReject(new HostError(code, detail));
  }

  private hello(): Hello {
    return appMessage.hello(this.instance, [...this.tools.keys()], this.resources.map((r) => r.declaration), this.opts.appVersion);
  }

  private repeatHello(hello: Hello, delayMs: number): void {
    this.send(hello);
    this.helloTimer = setTimeout(
      () => this.repeatHello(hello, Math.min(delayMs * 2, this.opts.helloMaxDelayMs ?? 5_000)),
      delayMs,
    );
  }

  private stopHello(): void {
    if (this.helloTimer) clearTimeout(this.helloTimer);
    this.helloTimer = null;
  }

  private send(message: AppToHost): void {
    if (!this.boot || this.closed) return;
    const envelope = sign(message, { key: this.boot.key, origin: this.ourOrigin(), nowMs: this.opts.nowMs?.() });
    this.peerWindow().postMessage(envelope, this.boot.peerOrigin);
  }

  private request(kind: RequestKind, params: unknown, options: { readonly signal?: AbortSignal } = {}): Promise<unknown> {
    if (this.closed) return Promise.reject(new HostError("closed", "the page closed"));
    if (this.generation === null) return Promise.reject(new HostError("not_ready", "the host has not accepted this page"));
    if (options.signal?.aborted) return Promise.reject(abortError());
    const id = `a-${++this.seq}`;
    const generation = this.generation;
    return new Promise((resolve, reject) => {
      const abort = () => {
        if (!this.pending.delete(id)) return;
        this.send(appMessage.cancel(generation, id, "app_aborted"));
        reject(abortError());
      };
      options.signal?.addEventListener("abort", abort, { once: true });
      const settle = () => options.signal?.removeEventListener("abort", abort);
      this.pending.set(id, {
        resolve: (value) => {
          settle();
          resolve(value);
        },
        reject: (error) => {
          settle();
          reject(error);
        },
      });
      this.send(appMessage.request(generation, id, kind, params));
    });
  }

  private onMessage(event: MessageEvent): void {
    const boot = this.boot;
    if (!boot || this.closed || event.source !== (this.peerWindow() as unknown) || event.origin !== boot.peerOrigin) return;
    let payload: unknown;
    try {
      payload = verify(event.data, { key: boot.key, expectedOrigin: boot.peerOrigin, nonceCache: this.nonces, nowMs: this.opts.nowMs?.() });
    } catch {
      this.counters.badEnvelope += 1;
      return;
    }
    const msg = parseHostMessage(payload);
    if (!msg) {
      this.counters.unknown += 1;
      return;
    }
    if (msg.t === "welcome") return this.onWelcome(msg);
    if (this.generation === null) {
      this.counters.early += 1; // nothing but `welcome` means anything before the host accepted the page
      return;
    }
    switch (msg.t) {
      case "call":
        return void this.onCall(msg);
      case "read":
        return void this.onRead(msg);
      case "cancel":
        if (msg.gen === this.generation) this.onHostCancel(msg.id);
        return;
      case "ping":
        if (msg.gen === this.generation) this.send(appMessage.pong(this.generation, msg.id));
        return;
      case "response": {
        const p = msg.gen === this.generation ? this.pending.get(msg.id) : undefined;
        if (!p) {
          this.counters.orphanResponses += 1;
          return;
        }
        this.pending.delete(msg.id);
        if (msg.error) p.reject(new HostError(msg.error.code, msg.error.message));
        else p.resolve(msg.result);
        return;
      }
    }
  }

  private onWelcome(w: Welcome): void {
    // Another page's, or a repeat for this one (the host re-sends it for a repeated hello).
    if (w.instance !== this.instance || this.generation !== null || this.statusValue !== "connecting") return;
    this.stopHello();
    if (this.readyTimer) clearTimeout(this.readyTimer);
    if (w.refused) return this.fail("needs-newer-talkie", w.refused.error.message, w.refused.error.code);
    this.generation = w.accepted.generation;
    this.capabilities = w.accepted.host;
    this.setStatus("connected");
    this.readyResolve(w.accepted.host);
  }

  /** Whether a `call`/`read` may run; a stale generation is refused, a duplicate id dropped. */
  private admit(m: CallMsg | ReadMsg): boolean {
    if (m.gen !== this.generation) {
      this.counters.stale += 1;
      this.send(appMessage.error(m.gen, m.id, PAGE_CODES.STALE_GENERATION, "a request for an earlier page generation"));
      return false;
    }
    if (this.inflight.has(m.id)) {
      this.counters.duplicates += 1;
      return false;
    }
    return true;
  }

  private async run(
    m: CallMsg | ReadMsg,
    work: (signal: AbortSignal) => Promise<McpResult> | McpResult,
    failed: (error: unknown) => AppToHost,
  ): Promise<void> {
    const controller = new AbortController();
    this.inflight.set(m.id, controller);
    let reply: AppToHost;
    try {
      reply = appMessage.result(m.gen, m.id, await work(controller.signal));
    } catch (error) {
      reply = failed(error);
    } finally {
      if (this.inflight.get(m.id) === controller) this.inflight.delete(m.id);
    }
    if (controller.signal.aborted) {
      this.counters.lateDropped += 1; // the host stopped waiting: a result nobody asked for is never sent
      return;
    }
    if (encodedBytes(reply) > MAX_MESSAGE_BYTES) {
      reply = appMessage.error(m.gen, m.id, PAGE_CODES.RESULT_TOO_LARGE, `the result exceeds ${MAX_MESSAGE_BYTES} bytes`);
    }
    this.send(reply);
  }

  private async onCall(m: CallMsg): Promise<void> {
    if (!this.admit(m)) return;
    const handler = this.tools.get(m.tool);
    if (!handler) {
      this.send(appMessage.error(m.gen, m.id, PAGE_CODES.UNKNOWN_TOOL, `no handler for ${m.tool}`));
      return;
    }
    await this.run(
      m,
      (signal) => handler(m.args, { signal, waitsOnPerson: m.waits_on_person }),
      (error) => appMessage.result(m.gen, m.id, toolErrorResult(error)),
    );
  }

  private async onRead(m: ReadMsg): Promise<void> {
    if (!this.admit(m)) return;
    const resource = this.resources.find((r) => declarationMatches(r.declaration, m.uri));
    if (!resource) {
      this.send(appMessage.error(m.gen, m.id, PAGE_CODES.UNKNOWN_RESOURCE, `no resource for ${m.uri}`));
      return;
    }
    const variables = templateVariables(resource.declaration, m.uri) ?? {};
    await this.run(
      m,
      (signal) => resource.handler(m.uri, { signal, variables }),
      (error) => appMessage.error(m.gen, m.id, PAGE_CODES.RESOURCE_UNAVAILABLE, errorText(error)),
    );
  }
}
